import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Play } from 'lucide-react';
import MultiSelectTicker from '../../MultiSelectTicker';
import AccordionFormSection from '../../Screener/AccordionFormSection';
import IntermediatePresetCard from '../../Screener/IntermediatePresetCard';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { cardToggleTemplate, actionButtonTemplate } from '../../../theme';
import {
  SPREAD_TYPES,
  SPREAD_TYPE_ORDER,
  spreadExpirationPresets,
  spreadAnchorPresets,
  spreadWidthPresets,
  spreadSpeedPresets,
  adjustDescriptionForPuts,
  buildSpreadPayload,
  countSpreadCombinations,
} from '../../../utils/spreadMappings';

/**
 * Parameter form for the Vertical Spread Simulator.
 *
 * Structured like the screener's Intermediate form — accordion sections,
 * multi-select preset cards, Cartesian expansion — but asks the two questions a
 * spread needs that a single contract doesn't: which leg anchors the position,
 * and how wide the spread is.
 *
 * Exposes a small imperative API (via ref) for the guided tour, mirroring the
 * screener forms: `getFormState`, only-if-empty `tourPopulate`, and
 * `tourSetSpreadType` for the tour's explicit spread-type choice buttons.
 */
const SpreadForm = forwardRef(function SpreadForm({ onRun, loading }, ref) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [selectedTickers, setSelectedTickers] = useState([]);
  const [spreadType, setSpreadType] = useState(null);
  const [selectedExpirations, setSelectedExpirations] = useState([]);
  const [selectedAnchors, setSelectedAnchors] = useState([]);
  const [selectedWidths, setSelectedWidths] = useState([]);
  const [speedKey, setSpeedKey] = useState('balanced');

  const [expandedSections, setExpandedSections] = useState(
    isMobile ? ['tickers'] : ['tickers', 'type', 'expiration', 'anchor', 'width']
  );
  const [completedSections, setCompletedSections] = useState(new Set());
  const [activeSection, setActiveSection] = useState('tickers');

  const SECTION_ORDER = ['tickers', 'type', 'expiration', 'anchor', 'width'];

  const config = spreadType ? SPREAD_TYPES[spreadType] : null;
  const isPut = config?.optionType === 'put';

  const isFormValid = () => (
    selectedTickers.length > 0
    && spreadType !== null
    && selectedExpirations.length > 0
    && selectedAnchors.length > 0
    && selectedWidths.length > 0
  );

  const totalCombinations = countSpreadCombinations(
    { expirations: selectedExpirations, anchors: selectedAnchors, widths: selectedWidths },
    selectedTickers
  );

  useImperativeHandle(ref, () => ({
    getFormState: () => ({
      selectedTickers,
      spreadType,
      selectedExpirations,
      selectedAnchors,
      selectedWidths,
      isValid: isFormValid(),
    }),
    // Guided-tour demo population. Applies `value` to `part` ONLY when that
    // part is currently empty — never overwrites the user's own selections.
    tourPopulate: (part, value) => {
      if (part === 'tickers' && selectedTickers.length === 0 && Array.isArray(value)) {
        setSelectedTickers(value.map((t) => ({ value: t, label: t })));
        setCompletedSections((prev) => new Set(prev).add('tickers'));
      } else if (part === 'type' && !spreadType && value) {
        setSpreadType(value);
        setCompletedSections((prev) => new Set(prev).add('type'));
      } else if (part === 'expiration' && selectedExpirations.length === 0 && Array.isArray(value)) {
        setSelectedExpirations(value);
        setCompletedSections((prev) => new Set(prev).add('expiration'));
      } else if (part === 'anchor' && selectedAnchors.length === 0 && Array.isArray(value)) {
        setSelectedAnchors(value);
        setCompletedSections((prev) => new Set(prev).add('anchor'));
      } else if (part === 'width' && selectedWidths.length === 0 && Array.isArray(value)) {
        setSelectedWidths(value);
        setCompletedSections((prev) => new Set(prev).add('width'));
      }
    },
    // The tour's spread-type choice buttons always apply: picking one there is
    // an explicit selection, so it may replace whatever is currently set.
    tourSetSpreadType: (key) => {
      if (!SPREAD_TYPES[key]) return;
      setSpreadType(key);
      setCompletedSections((prev) => new Set(prev).add('type'));
    },
  }));

  // --- Mobile auto-advance: open the next section as each one is answered ---
  const activateNext = (current, next) => {
    if (!isMobile) return;
    setCompletedSections((prev) => new Set(prev).add(current));
    if (next) {
      setExpandedSections([next]);
      setActiveSection(next);
    } else {
      setExpandedSections([]);
      setActiveSection('complete');
    }
  };

  useEffect(() => {
    if (isMobile && selectedTickers.length > 0 && activeSection === 'tickers') {
      activateNext('tickers', 'type');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTickers, isMobile]);

  useEffect(() => {
    if (isMobile && spreadType && activeSection === 'type') {
      activateNext('type', 'expiration');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadType, isMobile]);

  useEffect(() => {
    if (isMobile && selectedExpirations.length > 0 && activeSection === 'expiration') {
      activateNext('expiration', 'anchor');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExpirations, isMobile]);

  useEffect(() => {
    if (isMobile && selectedAnchors.length > 0 && activeSection === 'anchor') {
      activateNext('anchor', 'width');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAnchors, isMobile]);

  const isSectionDisabled = (section) => {
    if (!isMobile || completedSections.has(section)) return false;
    const index = SECTION_ORDER.indexOf(section);
    let highest = -1;
    SECTION_ORDER.forEach((s, i) => {
      if (completedSections.has(s)) highest = Math.max(highest, i);
    });
    return index > highest + 1;
  };

  const handleToggle = (section) => {
    if (isMobile) {
      setExpandedSections((prev) => (prev.includes(section) ? [] : [section]));
      if (SECTION_ORDER.includes(section)) setActiveSection(section);
    } else {
      setExpandedSections((prev) => (
        prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
      ));
    }
  };

  const toggleSelection = (setter, key, section) => {
    setter((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
    setCompletedSections((prev) => new Set(prev).add(section));
  };

  const handleRun = () => {
    const payload = buildSpreadPayload(
      {
        expirations: selectedExpirations,
        anchors: selectedAnchors,
        widths: selectedWidths,
        speedKey,
      },
      selectedTickers,
      spreadType
    );
    if (payload && onRun) onRun(payload);
  };

  const getDisabledMessage = () => {
    const missing = [];
    if (selectedTickers.length === 0) missing.push('Tickers');
    if (!spreadType) missing.push('Spread Type');
    if (selectedExpirations.length === 0) missing.push('Expiration');
    if (selectedAnchors.length === 0) missing.push('Anchor Strike');
    if (selectedWidths.length === 0) missing.push('Width');
    if (missing.length === 0) return '';
    if (missing.length === 1) return `Please select ${missing[0]}`;
    return 'Please complete all sections';
  };

  const summaryFor = (keys, presets, formatter) => {
    if (keys.length === 0) return '';
    if (keys.length === 1) return formatter(presets[keys[0]]);
    return `${keys.length} selected`;
  };

  return (
    <div className="spread-form">
      {/* 1. Tickers */}
      <AccordionFormSection
        title="Select Tickers"
        icon=""
        summary={selectedTickers.map((t) => t.value).join(', ')}
        isExpanded={expandedSections.includes('tickers')}
        isDisabled={isSectionDisabled('tickers')}
        isCompleted={completedSections.has('tickers')}
        onToggle={() => handleToggle('tickers')}
        isMobile={isMobile}
        dataTour="spread-tickers"
      >
        <MultiSelectTicker
          value={selectedTickers}
          onChange={(tickers) => {
            setSelectedTickers(tickers || []);
            if (tickers?.length) setCompletedSections((p) => new Set(p).add('tickers'));
          }}
          placeholder="Search and select tickers (e.g., AAPL, MSFT, NVDA)..."
          controlBackground="#1f2937"
        />
      </AccordionFormSection>

      {/* 2. Spread type — the direction choice, expressed as a strategy */}
      <AccordionFormSection
        title="Spread Type"
        icon=""
        summary={config ? `${config.direction} — ${config.label}` : ''}
        isExpanded={expandedSections.includes('type')}
        isDisabled={isSectionDisabled('type')}
        isCompleted={completedSections.has('type')}
        onToggle={() => handleToggle('type')}
        isMobile={isMobile}
        dataTour="spread-type"
      >
        <p className="section-helper-text mb-3">
          Pick your outlook and how you want to pay for it — debit spreads cost money
          up front, credit spreads collect it.
        </p>
        <div className="direction-grid grid-cols-2 gap-3">
          {SPREAD_TYPE_ORDER.map((key) => {
            const t = SPREAD_TYPES[key];
            const selected = spreadType === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setSpreadType(key);
                  setCompletedSections((p) => new Set(p).add('type'));
                }}
                className={`${cardToggleTemplate.cssClasses.directionBase} ${selected ? cardToggleTemplate.cssClasses.selected : ''}`}
                title={t.tooltip}
              >
                <span className={cardToggleTemplate.cssClasses.directionTitle}>
                  {t.direction}
                </span>
                <span className={cardToggleTemplate.cssClasses.directionSubtitle}>
                  {t.label}
                </span>
                <span className="text-xs text-gray-400 mt-1">{t.subtitle}</span>
                <span
                  className="text-xs mt-1"
                  style={{ color: t.isCredit ? '#10b981' : '#218aff' }}
                >
                  {t.entryType} · {t.riskNote}
                </span>
              </button>
            );
          })}
        </div>
      </AccordionFormSection>

      {/* 3. Expiration window */}
      <AccordionFormSection
        title="Expiration Window"
        icon=""
        summary={summaryFor(selectedExpirations, spreadExpirationPresets, (p) => p?.label || '')}
        isExpanded={expandedSections.includes('expiration')}
        isDisabled={isSectionDisabled('expiration')}
        isCompleted={completedSections.has('expiration')}
        onToggle={() => handleToggle('expiration')}
        isMobile={isMobile}
        dataTour="spread-expiration"
      >
        <p className="section-helper-text">Both legs share this expiration.</p>
        <div className="intermediate-preset-grid">
          {Object.entries(spreadExpirationPresets).map(([key, preset]) => (
            <IntermediatePresetCard
              key={key}
              presetKey={key}
              label={preset.label}
              description={preset.description}
              tooltip={preset.tooltip}
              isSelected={selectedExpirations.includes(key)}
              onClick={() => toggleSelection(setSelectedExpirations, key, 'expiration')}
            />
          ))}
        </div>
      </AccordionFormSection>

      {/* 4. Anchor strike */}
      <AccordionFormSection
        title="Anchor Strike"
        icon=""
        summary={summaryFor(
          selectedAnchors,
          spreadAnchorPresets,
          (p) => `${p?.label || ''} ${adjustDescriptionForPuts(p?.description, isPut) || ''}`.trim()
        )}
        isExpanded={expandedSections.includes('anchor')}
        isDisabled={isSectionDisabled('anchor')}
        isCompleted={completedSections.has('anchor')}
        onToggle={() => handleToggle('anchor')}
        isMobile={isMobile}
        dataTour="spread-anchor"
      >
        <p className="section-helper-text">
          {config
            ? `Where to place the ${config.isCredit ? 'short' : 'long'} leg — the one nearest the money.`
            : 'Where to place the leg nearest the money.'}
        </p>
        <div className="intermediate-preset-grid">
          {Object.entries(spreadAnchorPresets).map(([key, preset]) => (
            <IntermediatePresetCard
              key={key}
              presetKey={key}
              label={preset.label}
              description={adjustDescriptionForPuts(preset.description, isPut)}
              tooltip={preset.tooltip}
              isSelected={selectedAnchors.includes(key)}
              onClick={() => toggleSelection(setSelectedAnchors, key, 'anchor')}
            />
          ))}
        </div>
      </AccordionFormSection>

      {/* 5. Width */}
      <AccordionFormSection
        title="Spread Width"
        icon=""
        summary={summaryFor(
          selectedWidths,
          spreadWidthPresets,
          (p) => `${p?.label || ''} ${p?.description || ''}`.trim()
        )}
        isExpanded={expandedSections.includes('width')}
        isDisabled={isSectionDisabled('width')}
        isCompleted={completedSections.has('width')}
        onToggle={() => handleToggle('width')}
        isMobile={isMobile}
        dataTour="spread-width"
      >
        <p className="section-helper-text">
          How far the second leg sits from the anchor, as a % of the stock price.
          Wider costs more but pays more.
        </p>
        <div className="intermediate-preset-grid">
          {Object.entries(spreadWidthPresets).map(([key, preset]) => (
            <IntermediatePresetCard
              key={key}
              presetKey={key}
              label={preset.label}
              description={preset.description}
              tooltip={preset.tooltip}
              isSelected={selectedWidths.includes(key)}
              onClick={() => toggleSelection(setSelectedWidths, key, 'width')}
            />
          ))}
        </div>
      </AccordionFormSection>

      {/* 6. Exit timing — single-select, so it stays out of the combination count */}
      <AccordionFormSection
        title="Exit Timing"
        icon=""
        summary={spreadSpeedPresets[speedKey]?.label || ''}
        isExpanded={expandedSections.includes('speed')}
        isDisabled={false}
        isCompleted={false}
        onToggle={() => handleToggle('speed')}
        isMobile={isMobile}
        dataTour="spread-speed"
      >
        <p className="section-helper-text">
          When you plan to close. You can change this after the run with the
          Days-to-Exit slider.
        </p>
        <div className="intermediate-preset-grid">
          {Object.entries(spreadSpeedPresets).map(([key, preset]) => (
            <IntermediatePresetCard
              key={key}
              presetKey={key}
              label={preset.label}
              description={preset.description}
              tooltip={preset.tooltip}
              isSelected={speedKey === key}
              onClick={() => setSpeedKey(key)}
            />
          ))}
        </div>
      </AccordionFormSection>

      {/* Run */}
      <div className={isMobile ? 'fixed-bottom-button' : 'inline-button'} data-tour="spread-actions">
        <button
          type="button"
          onClick={handleRun}
          disabled={!isFormValid() || loading}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.runSolid} w-full ${!isFormValid() ? actionButtonTemplate.cssClasses.disabled : ''}`}
          style={{ position: 'relative' }}
          title="Simulate these spreads"
        >
          {loading ? (
            <span className="spinner" />
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Play size={14} style={{ fill: 'currentColor' }} />
              {totalCombinations > 1
                ? `Simulate ${totalCombinations} Spreads`
                : 'Simulate Spread'}
            </span>
          )}
          {!isFormValid() && (
            <span className={actionButtonTemplate.cssClasses.tooltip}>
              {getDisabledMessage()}
            </span>
          )}
        </button>
      </div>
    </div>
  );
});

export default SpreadForm;
